import { Injectable } from '@angular/core';
import { GridColumnStateModel } from 'src/app/shared/models/grid-column-state.model';
import { FetchArgs } from 'src/app/shared/models/core';
import { IdentityService } from 'src/app/core/identity.service';

@Injectable({
    providedIn: 'root'
})
export class GridComponentService {
    private _userId: number | null = null;

    get userId() {
        const identity = this.identityService.current;
        return identity ? Number(identity.contactId) : null;
    }

    constructor(protected identityService: IdentityService) {
        this._userId = identityService.current?.contactId ?? null;
    }

    saveColumns(cmpName: string, colStates: GridColumnStateModel[]): void {
        localStorage.setItem(this.getLocalStorageColumnsPath(cmpName), JSON.stringify({ colStates: colStates }));
    }

    saveFetchArgs(cmpName: string, fetchArgs: FetchArgs, userId?: number): void {
        localStorage.setItem(
            this.getLocalStorageFetchArgsPath(cmpName, userId),
            JSON.stringify({ fetchArgs: fetchArgs })
        );
    }

    saveAsyncFiltersForField(cmpName: string, fieldName: string, selectedItems: any[]): void {
        const filterVals = this.getAsyncFilters(cmpName);
        filterVals[fieldName] = selectedItems;
        localStorage.setItem(this.getLocalStorageAsyncFilterValuesPath(cmpName), JSON.stringify(filterVals));
    }

    saveLookupsForFilter(gridName: string, fieldName: string, valueField: string, items: unknown[]): void {
        if (!this.isArrayOfObjects(items)) {
            return;
        }
        // Get the current filter values from storage
        const existingJson = localStorage.getItem(this.getLocalStorageFilterLookupsPath(gridName, fieldName));
        const existingLookups = existingJson ? JSON.parse(existingJson) : {};

        // Create an object to store them based on a key, value field
        const filterVals = items.reduce((acc, item: unknown) => {
            if (item && item[valueField] !== undefined) {
                acc[item[valueField]] = item;
            }
            return acc;
        }, existingLookups);

        // Store the object back in storage
        localStorage.setItem(this.getLocalStorageFilterLookupsPath(gridName, fieldName), JSON.stringify(filterVals));
    }

    replaceLookupValues(gridName: string, fieldName: string, inputArray: unknown[]): unknown[] {
        const lookupJson = localStorage.getItem(this.getLocalStorageFilterLookupsPath(gridName, fieldName));

        if (!lookupJson) {
            return inputArray; // Return original array if no lookup data found
        }

        const lookupData = JSON.parse(lookupJson);

        return inputArray.map((value) => {
            if (typeof value === 'number') {
                const lookupItem = lookupData[value];
                return lookupItem !== undefined ? lookupItem : value;
            }
            return value; // Return non-numeric values unchanged
        });
    }

    getColumns(cmpName: string): GridColumnStateModel[] {
        let colStatesJson = null;
        colStatesJson = localStorage.getItem(this.getLocalStorageColumnsPath(cmpName));

        if (colStatesJson !== null) {
            const obj = JSON.parse(colStatesJson);
            if (obj.colStates) {
                return obj.colStates;
            }
        }

        return null;
    }

    getFetchArgs(cmpName: string): FetchArgs {
        let fetchArgsJson = null;

        fetchArgsJson = localStorage.getItem(this.getLocalStorageFetchArgsPath(cmpName));

        if (fetchArgsJson !== null) {
            const obj = JSON.parse(fetchArgsJson);
            if (obj.fetchArgs) {
                return obj.fetchArgs;
            }
        }
        return null;
    }

    getAsyncFilters(cmpName: string, fieldName?: string): any {
        let asyncFiltersJson = null;

        asyncFiltersJson = localStorage.getItem(this.getLocalStorageAsyncFilterValuesPath(cmpName));

        if (asyncFiltersJson !== null) {
            const asyncFiltersObj = JSON.parse(asyncFiltersJson);
            return fieldName ? asyncFiltersObj[fieldName] : asyncFiltersObj;
        }
        return fieldName ? null : {};
    }

    clearColumnsState(cmpName: string): void {
        localStorage.removeItem(this.getLocalStorageColumnsPath(cmpName));
    }

    clearFetchArgsState(cmpName: string): void {
        localStorage.removeItem(this.getLocalStorageFetchArgsPath(cmpName));
    }

    clearFilterLookup(cmpName: string, fieldName: string): void {
        localStorage.removeItem(this.getLocalStorageFilterLookupsPath(cmpName, fieldName));
    }

    clearAsyncFilters(cmpName: string): void {
        localStorage.removeItem(this.getLocalStorageAsyncFilterValuesPath(cmpName));
    }

    private getLocalStorageColumnsPath(cmpName: string): string {
        return `${this.userId ?? this._userId}.Columns.${cmpName}`;
    }

    private getLocalStorageFetchArgsPath(cmpName: string, userId?: number): string {
        return `${this.userId ?? userId ?? this._userId}.FetchArgs.${cmpName}`;
    }

    private getLocalStorageAsyncFilterValuesPath(cmpName: string): string {
        return `${this.userId ?? this._userId}.AsyncFilterValues.${cmpName}`;
    }

    private getLocalStorageFilterLookupsPath(gridName: string, fieldName): string {
        return `${this.userId ?? this._userId}.Lookups.${gridName}.${fieldName}`;
    }

    private isArrayOfObjects = (value: unknown): value is object[] =>
        Array.isArray(value) && value.every((item) => item !== null && typeof item === 'object');
}
